import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEffortsBandEmployees, useEffortsInsightEmployees } from '../hooks/useEffortsDashboard';
import { formatByType, formatNumber, formatPercent } from '../../../utils/dashboardFormatters';

/**
 * Per-metric drill-down presentation config: the short column header for the
 * metric value, and how to render that value in each row. Keyed by BOTH the
 * camelCase card id (KPI-tile drills) and the snake_case API metric (insight
 * {metric,band} drills), so either form resolves the right column.
 */
const METRIC_CONFIG = {
  workingDays: { valueLabel: 'Avg WD', renderValue: (v) => (v == null ? '—' : formatNumber(v)) },
  working_days: { valueLabel: 'Avg WD', renderValue: (v) => (v == null ? '—' : formatNumber(v)) },
  drCallAverage: { valueLabel: 'Dr Call Avg', renderValue: (v) => (v == null ? '—' : Number(v).toFixed(1)) },
  dr_call_avg: { valueLabel: 'Dr Call Avg', renderValue: (v) => (v == null ? '—' : Number(v).toFixed(1)) },
  coverage: { valueLabel: 'Coverage %', renderValue: (v) => (v == null ? '—' : formatPercent(v)) },
  compliance: { valueLabel: 'Compliance %', renderValue: (v) => (v == null ? '—' : formatPercent(v)) },
};

/** Column count used to span loading/empty rows across the full table width. */
const COLUMN_COUNT = 4;

/** Reference .tbl th style: uppercase, small, grey, sticky. */
const HEADER_CELL_SX = {
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '.3px',
  color: '#6B7280',
  bgcolor: '#f4f7fc',
  borderBottom: '2px solid #e2e8f2',
};

/** Format a date as YYYY-MM-DD (reference modal shows ISO dates). */
function formatIsoDate(value) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return typeof value === 'string' ? value : '—';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function LoadingRows({ rows = 6 }) {
  return (
    <TableBody>
      {Array.from({ length: rows }).map((_, i) => (
        <TableRow key={i}>
          {Array.from({ length: COLUMN_COUNT }).map((__, j) => (
            <TableCell key={j}>
              <Skeleton variant="text" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  );
}

/**
 * Click-to-drill-down modal, shared by two sources:
 *  - a band of one of the four KPI cards (`metricId` + `band`), and
 *  - a preset insight (`preset`), which hits insight-employees and uses the
 *    backend-provided metricLabel / metricFormat for the last column.
 *
 * Exactly one source should be active per open. The parent controls `open`.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string|null} [props.metricId] - Band mode: card id.
 * @param {string} [props.metricLabel] - Band mode: human label of the KPI card.
 * @param {number|null} [props.band] - Band mode: band index 0..3.
 * @param {string} [props.bandLabel] - Band mode: band label (e.g. "<20 Days").
 * @param {number} [props.count] - Band mode: the band count shown on the card.
 * @param {string|null} [props.preset] - Preset mode: insight preset id.
 * @param {string} [props.title] - Preset mode: title to show (insight text).
 */
export default function BandDrilldownModal({
  open,
  onClose,
  metricId = null,
  metricLabel,
  band = null,
  bandLabel,
  count,
  preset = null,
  title: presetTitle,
}) {
  const isPreset = Boolean(preset);

  // Only the active mode's query is enabled, so the inactive one never fetches.
  const bandQuery = useEffortsBandEmployees(metricId, band, {
    enabled: open && !isPreset,
  });
  const presetQuery = useEffortsInsightEmployees(preset, {
    enabled: open && isPreset,
  });

  const { data, isLoading, isError } = isPreset ? presetQuery : bandQuery;

  const employees = data?.employees ?? [];

  // Resolve the last column's header + value renderer per mode.
  let valueLabel;
  let renderValue;
  if (isPreset) {
    valueLabel = data?.metricLabel || 'Value';
    const fmt = data?.metricFormat || 'number';
    renderValue = (v) => (v == null ? '–' : formatByType(v, fmt));
  } else {
    const config = METRIC_CONFIG[metricId] ?? {
      valueLabel: 'Value',
      renderValue: (v) => (v == null ? '–' : String(v)),
    };
    valueLabel = config.valueLabel;
    renderValue = config.renderValue;
  }

  // Title format: "<modalTitle> — N FLS". The backend modalTitle arrives via
  // `title` (preset drills) or `metricLabel` (insight band drills). KPI-tile
  // band drills have no modalTitle, so fall back to "<metric> · <band>".
  const resolvedTotal = isPreset
    ? data?.total
    : (data?.total ?? count ?? employees.length);
  let base;
  if (isPreset) {
    base = presetTitle || 'Insight';
  } else if (metricLabel && !bandLabel && !data?.bandLabel) {
    base = metricLabel; // insight-driven band drill → backend modalTitle
  } else {
    const resolvedBandLabel = data?.bandLabel || bandLabel || '';
    base = `${metricLabel || 'Band'}${resolvedBandLabel ? ` · ${resolvedBandLabel}` : ''}`;
  }
  const title = `${base}${resolvedTotal != null ? ` — ${formatNumber(resolvedTotal)} FLS` : ''}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      // Blurred dark backdrop (.modal-overlay { background:rgba(15,30,61,.45); backdrop-filter:blur(3px) }).
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(15,30,61,.45)',
            backdropFilter: 'blur(3px)',
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 20px 60px rgba(15,30,61,.3)',
        },
      }}
    >
      {/* Navy header bar, bold white title + X (.modal-head). */}
      <DialogTitle
        sx={{
          m: 0,
          px: '18px',
          py: '13px',
          bgcolor: '#0F1E3D',
          color: '#fff',
          fontSize: '15px',
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Box component="span" sx={{ minWidth: 0 }}>
          {title}
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: '#cbd3e3', p: 0.5, flex: '0 0 auto' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Body: padding + a bordered, rounded table box (.modal-body + .table-wrap). */}
      <DialogContent sx={{ p: '14px 18px' }}>
        {isError ? (
          <Alert severity="error">Failed to load field users.</Alert>
        ) : (
          <TableContainer
            sx={{
              maxHeight: 520,
              border: '1px solid #E4E8EE',
              borderRadius: '10px',
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Field User (FLS)', 'MIS HQ', 'Date of Joining'].map((h) => (
                    <TableCell key={h} sx={HEADER_CELL_SX}>
                      {h}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={HEADER_CELL_SX}>
                    {valueLabel}
                  </TableCell>
                </TableRow>
              </TableHead>

              {isLoading ? (
                <LoadingRows />
              ) : (
                <TableBody>
                  {employees.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={COLUMN_COUNT}>
                        <Box sx={{ py: 2 }}>
                          <Alert severity="info" sx={{ m: 0 }}>
                            No field users to show.
                          </Alert>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    employees.map((emp) => (
                      <TableRow key={emp.employeeId ?? `${emp.name}-${emp.misHq}`} hover>
                        <TableCell sx={{ borderColor: '#E4E8EE' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#0F1E3D' }}>
                            {emp.name ?? '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ borderColor: '#E4E8EE' }}>{emp.misHq ?? '—'}</TableCell>
                        <TableCell sx={{ borderColor: '#E4E8EE' }}>
                          {formatIsoDate(emp.dateOfJoining)}
                        </TableCell>
                        <TableCell align="right" sx={{ borderColor: '#E4E8EE' }}>
                          {renderValue(emp.metricValue)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              )}
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
}
